import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { BrandWordmark } from "@/components/BrandWordmark";
import { COMPARISONS } from "@/data/comparisons";

const LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
    { "@type": "ListItem", position: 2, name: "Compare", item: "https://www.videoileo.com/compare" },
  ],
};

export default function Compare() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="compare-index-page">
      <Seo
        title="Compare — Videoileo vs Alternatives"
        description="How Videoileo compares to InVideo AI, Pictory, Fliki and AutoShorts.ai for faceless YouTube channels: scripts, visuals, captions, autopilot and pricing."
        path="/compare"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(LD)}</script>
      </Helmet>

      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center"><BrandWordmark className="h-12" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <Link to="/register" data-testid="compare-index-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">Start free</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Compare</p>
        <h1 className="font-head text-4xl sm:text-5xl tracking-tighter font-black mb-4">Videoileo vs the alternatives</h1>
        <p className="text-zinc-400 max-w-2xl mb-10">Honest, feature-by-feature comparisons for creators choosing a faceless video tool in 2026.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {COMPARISONS.map((c) => (
            <Link
              key={c.slug}
              to={`/compare/${c.slug}`}
              data-testid={`compare-card-${c.slug}`}
              className="group border border-white/10 rounded-md bg-[#121212] p-6 hover:border-blue-500/40 transition-colors"
            >
              <h2 className="font-head text-xl font-semibold mb-1.5">Videoileo vs {c.name}</h2>
              <p className="text-sm text-zinc-500 mb-4">{c.tagline}</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-400 group-hover:text-blue-300 transition-colors">
                Read the comparison <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
